import React from "react";
import { Formik } from "formik";

import { isValidAssetReference } from "./validation";
import {
  ActionRow,
  FieldGroup,
  FieldLabel,
  FormGrid,
  FormStack,
  PrimaryButton,
  SecondaryButton,
  SelectInput,
  TextInput,
  TextareaInput,
  ValidationErrorText,
  WideField,
} from "./styles/admin-forms.styles";
import {
  DynamicTextRowsField,
  ImagePickerField,
  MetadataRows,
  MetadataToggle,
} from "./shared-form-fields";
import { MerchandiseFormValues, MetadataRow } from "./types";
import { buildMetadataFromRows, extractMetadataRows, normalizeGalleryImages } from "./utils";

type MerchandiseEditorFormState = Omit<MerchandiseFormValues, "metadata"> & {
  metadata_rows: MetadataRow[];
  signed: boolean;
  featured: boolean;
};

type MerchandiseEditorFormErrors = Partial<
  Record<keyof MerchandiseEditorFormState | "gallery_images" | "image", string>
>;

const toInitialState = (initialValues: MerchandiseFormValues): MerchandiseEditorFormState => ({
  ...initialValues,
  category: initialValues.category || "",
  description: initialValues.description || "",
  featured: Boolean(initialValues.metadata?.featured),
  gallery_images: normalizeGalleryImages(initialValues.gallery_images),
  metadata_rows: extractMetadataRows(initialValues.metadata),
  signed: Boolean(initialValues.metadata?.signed),
});

const trimOptionalValue = (value?: string) => {
  const trimmed = value?.trim() || "";
  return trimmed || undefined;
};

const normalizeMerchandiseValues = (values: MerchandiseEditorFormState): MerchandiseFormValues => {
  const { featured, metadata_rows, signed, ...baseValues } = values;

  return {
    ...baseValues,
    category: values.category.trim(),
    description: values.description.trim(),
    gallery_images: (baseValues.gallery_images || []).map((entry) => entry.trim()).filter(Boolean),
    long_description: trimOptionalValue(values.long_description),
    metadata: buildMetadataFromRows(metadata_rows, {
      featured,
      signed,
    }),
    name: values.name.trim(),
    stock: values.stock === "" ? "" : values.stock,
  };
};

const validateMerchandiseValues = (
  values: MerchandiseEditorFormState,
): MerchandiseEditorFormErrors => {
  const errors: MerchandiseEditorFormErrors = {};
  const numericPrice = Number(values.price);
  const numericStock = values.stock === "" ? null : Number(values.stock);
  const nonEmptyGalleryImages = (values.gallery_images || []).filter((entry) => entry.trim());

  if (!values.player_id) {
    errors.player_id = "Select a player before saving merchandise.";
  }

  if (!values.name.trim()) {
    errors.name = "Merchandise name is required.";
  }

  if (!values.category.trim()) {
    errors.category = "Category is required.";
  }

  if (values.price === "" || !Number.isFinite(numericPrice)) {
    errors.price = "Price is required.";
  } else if (numericPrice < 0) {
    errors.price = "Price must be zero or greater.";
  }

  if (numericStock !== null) {
    if (!Number.isFinite(numericStock) || numericStock < 0) {
      errors.stock = "Stock must be zero or greater.";
    } else if (!Number.isInteger(numericStock)) {
      errors.stock = "Stock must be a whole number.";
    }
  }

  if (
    typeof values.image === "string" &&
    values.image.trim() &&
    !isValidAssetReference(values.image)
  ) {
    errors.image = "Primary image must be an upload or a valid asset URL.";
  }

  if (nonEmptyGalleryImages.some((entry) => !isValidAssetReference(entry))) {
    errors.gallery_images = "Each gallery image must be a valid asset URL.";
  }

  return errors;
};

export const MerchandiseEditorForm: React.FC<{
  initialValues: MerchandiseFormValues;
  isSubmitting?: boolean;
  onCancel: () => void;
  onSubmit: (values: MerchandiseFormValues) => Promise<void> | void;
  playerOptions: { label: string; value: string }[];
  submitLabel?: string;
}> = ({ initialValues, isSubmitting = false, onCancel, onSubmit, playerOptions, submitLabel }) => (
  <Formik<MerchandiseEditorFormState>
    enableReinitialize
    initialValues={toInitialState(initialValues)}
    validate={validateMerchandiseValues}
    onSubmit={async (values) => {
      await Promise.resolve(onSubmit(normalizeMerchandiseValues(values)));
    }}
  >
    {({
      errors,
      handleSubmit,
      isSubmitting: formikSubmitting,
      setFieldTouched,
      setFieldValue,
      submitCount,
      touched,
      values,
    }) => {
      const fieldIds = {
        category: "merchandise-category",
        description: "merchandise-description",
        longDescription: "merchandise-long-description",
        name: "merchandise-name",
        player: "merchandise-player",
        price: "merchandise-price",
        stock: "merchandise-stock",
      } as const;

      const getError = (
        fieldName: keyof MerchandiseEditorFormState | "gallery_images" | "image",
      ) => {
        const errorValue = errors[fieldName];
        const touchedValue = touched[fieldName as keyof typeof touched];

        return typeof errorValue === "string" && (touchedValue || submitCount > 0)
          ? errorValue
          : undefined;
      };

      return (
        <form onSubmit={handleSubmit}>
          <FormStack>
            <FormGrid>
              <WideField>
                <FieldGroup>
                  <FieldLabel htmlFor={fieldIds.player}>Player</FieldLabel>
                  <SelectInput
                    aria-invalid={Boolean(getError("player_id"))}
                    id={fieldIds.player}
                    name="player_id"
                    onBlur={() => setFieldTouched("player_id", true, false)}
                    onChange={(event) => setFieldValue("player_id", event.target.value)}
                    required
                    value={values.player_id || ""}
                  >
                    <option value="">Select a player</option>
                    {playerOptions.map((player) => (
                      <option key={player.value} value={player.value}>
                        {player.label}
                      </option>
                    ))}
                  </SelectInput>
                  {getError("player_id") ? (
                    <ValidationErrorText>{getError("player_id")}</ValidationErrorText>
                  ) : null}
                </FieldGroup>
              </WideField>

              <WideField>
                <FieldGroup>
                  <FieldLabel htmlFor={fieldIds.name}>Name</FieldLabel>
                  <TextInput
                    aria-invalid={Boolean(getError("name"))}
                    id={fieldIds.name}
                    name="name"
                    onBlur={() => setFieldTouched("name", true, false)}
                    onChange={(event) => setFieldValue("name", event.target.value)}
                    required
                    value={values.name || ""}
                  />
                  {getError("name") ? (
                    <ValidationErrorText>{getError("name")}</ValidationErrorText>
                  ) : null}
                </FieldGroup>
              </WideField>

              <WideField>
                <FieldGroup>
                  <FieldLabel htmlFor={fieldIds.description}>Description</FieldLabel>
                  <TextareaInput
                    id={fieldIds.description}
                    name="description"
                    onBlur={() => setFieldTouched("description", true, false)}
                    onChange={(event) => setFieldValue("description", event.target.value)}
                    value={values.description || ""}
                  />
                </FieldGroup>
              </WideField>

              <WideField>
                <FieldGroup>
                  <FieldLabel htmlFor={fieldIds.longDescription}>Long description</FieldLabel>
                  <TextareaInput
                    id={fieldIds.longDescription}
                    name="long_description"
                    onBlur={() => setFieldTouched("long_description", true, false)}
                    onChange={(event) => setFieldValue("long_description", event.target.value)}
                    value={values.long_description || ""}
                  />
                </FieldGroup>
              </WideField>

              <FieldGroup>
                <FieldLabel htmlFor={fieldIds.price}>Price</FieldLabel>
                <TextInput
                  aria-invalid={Boolean(getError("price"))}
                  id={fieldIds.price}
                  min="0"
                  name="price"
                  onBlur={() => setFieldTouched("price", true, false)}
                  onChange={(event) => setFieldValue("price", event.target.value)}
                  required
                  type="number"
                  value={values.price}
                />
                {getError("price") ? (
                  <ValidationErrorText>{getError("price")}</ValidationErrorText>
                ) : null}
              </FieldGroup>

              <FieldGroup>
                <FieldLabel htmlFor={fieldIds.stock}>Stock</FieldLabel>
                <TextInput
                  aria-invalid={Boolean(getError("stock"))}
                  id={fieldIds.stock}
                  min="0"
                  name="stock"
                  onBlur={() => setFieldTouched("stock", true, false)}
                  onChange={(event) => setFieldValue("stock", event.target.value)}
                  type="number"
                  value={values.stock}
                />
                {getError("stock") ? (
                  <ValidationErrorText>{getError("stock")}</ValidationErrorText>
                ) : null}
              </FieldGroup>

              <FieldGroup>
                <FieldLabel htmlFor={fieldIds.category}>Category</FieldLabel>
                <TextInput
                  aria-invalid={Boolean(getError("category"))}
                  id={fieldIds.category}
                  name="category"
                  onBlur={() => setFieldTouched("category", true, false)}
                  onChange={(event) => setFieldValue("category", event.target.value)}
                  required
                  value={values.category || ""}
                />
                {getError("category") ? (
                  <ValidationErrorText>{getError("category")}</ValidationErrorText>
                ) : null}
              </FieldGroup>
            </FormGrid>

            <div onBlur={() => setFieldTouched("image", true, false)}>
              <ImagePickerField
                helper="Primary product image supports direct upload, including replacements."
                label="Primary image"
                onChange={(value) => {
                  setFieldTouched("image", true, false);
                  setFieldValue("image", value);
                }}
                placeholder="https://example.com/merchandise.jpg"
                value={values.image}
              />
              {getError("image") ? (
                <ValidationErrorText>{getError("image")}</ValidationErrorText>
              ) : null}
            </div>

            <div onBlur={() => setFieldTouched("gallery_images", true, false)}>
              <DynamicTextRowsField
                addLabel="Add gallery image"
                helper="Gallery images stay URL-based so merch details can remain flexible."
                label="Gallery images"
                onChange={(rows) => {
                  setFieldTouched("gallery_images", true, false);
                  setFieldValue("gallery_images", rows);
                }}
                placeholder="https://example.com/gallery-image.jpg"
                rows={values.gallery_images || [""]}
              />
              {getError("gallery_images") ? (
                <ValidationErrorText>{getError("gallery_images")}</ValidationErrorText>
              ) : null}
            </div>

            <FormGrid>
              <MetadataToggle
                checked={values.signed}
                label="Signed item"
                onChange={(checked) => setFieldValue("signed", checked)}
              />
              <MetadataToggle
                checked={values.featured}
                label="Featured item"
                onChange={(checked) => setFieldValue("featured", checked)}
              />
            </FormGrid>

            <MetadataRows
              onChange={(rows) => setFieldValue("metadata_rows", rows)}
              rows={values.metadata_rows}
            />

            <ActionRow>
              <SecondaryButton onClick={onCancel} type="button">
                Cancel
              </SecondaryButton>
              <PrimaryButton disabled={isSubmitting || formikSubmitting} type="submit">
                {isSubmitting || formikSubmitting ? "Saving..." : submitLabel || "Save item"}
              </PrimaryButton>
            </ActionRow>
          </FormStack>
        </form>
      );
    }}
  </Formik>
);
